import { supabase } from '@/lib/supabase';
import type { AuthUser, BranchAssignment, UserRole } from '../types/auth';

interface ProfileRow {
  id: string;
  role: UserRole;
  display_name: string | null;
  is_active: boolean;
}

async function loadBranchAssignments(
  userId: string,
  role: UserRole
): Promise<{ branchIds: number[]; primaryBranchId: number | null }> {
  if (role === 'admin') {
    const { data } = await supabase.from('branches').select('id').order('sort_order');
    const ids = (data ?? []).map((b) => b.id as number);
    return { branchIds: ids, primaryBranchId: ids[0] ?? null };
  }

  if (role === 'staff') {
    const { data } = await supabase
      .from('staff_branch_assignments')
      .select('branch_id, is_primary')
      .eq('user_id', userId);

    const assignments = (data ?? []) as { branch_id: number; is_primary: boolean }[];
    const branchIds = assignments.map((a) => a.branch_id);
    const primary = assignments.find((a) => a.is_primary);
    return {
      branchIds,
      primaryBranchId: primary?.branch_id ?? branchIds[0] ?? null,
    };
  }

  return { branchIds: [], primaryBranchId: null };
}

export async function buildAuthUser(
  authUser: { id: string; email?: string; email_confirmed_at?: string | null }
): Promise<AuthUser | null> {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('id, role, display_name, is_active')
    .eq('id', authUser.id)
    .single();

  if (error || !profile) return null;

  const row = profile as ProfileRow;
  const { branchIds, primaryBranchId } = await loadBranchAssignments(row.id, row.role);

  return {
    id: row.id,
    email: authUser.email ?? '',
    role: row.role,
    displayName: row.display_name,
    isActive: row.is_active,
    emailVerified: !!authUser.email_confirmed_at,
    branchIds,
    primaryBranchId,
  };
}

export async function signInWithPassword(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email: email.trim(), password });
}

export async function signUpWithPassword(
  email: string,
  password: string,
  displayName?: string
) {
  return supabase.auth.signUp({
    email: email.trim(),
    password,
    options: {
      data: { display_name: displayName?.trim() || undefined },
      emailRedirectTo: `${window.location.origin}${import.meta.env.BASE_URL || '/'}auth/callback`,
    },
  });
}

export async function signOut() {
  return supabase.auth.signOut();
}

export type { BranchAssignment };
